import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSCallbacks: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Callbacks</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    JavaScript&apos;s first answer to asynchronous work: pass a function that runs &quot;when the task is done.&quot; Simple and everywhere — and the reason promises were later invented.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="17 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>We learned that JavaScript is single-threaded and can&apos;t block while waiting for slow operations. Now consider a food delivery app — placing an order triggers a strict sequence:</Para>
   <CodeBlock snippets={[{ language: 'text', code: `Validate user\n   ↓\nCreate order\n   ↓\nProcess payment\n   ↓\nAssign delivery partner\n   ↓\nSend notification` }]} defaultLanguage="text" id="cb-steps" />
   <Para>Payment should start only after the order is created; the driver only after payment succeeds; the notification only after the driver is assigned. We need a mechanism that says <Mark>&quot;when this task finishes, execute this code.&quot;</Mark> That mechanism is a <Mark>callback</Mark>.</Para>
  </section>

  {/* 2. Why Existing Solutions Fail */}
  <section className="mb-12">
   <SectionHeading n={2}>Why Existing Solutions Fail</SectionHeading>
   <Para>Suppose we just call the steps in order:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `createOrder();\nprocessPayment();\nassignDriver();` }]} defaultLanguage="javascript" id="cb-naive" />
   <Para>It looks fine — but what if <Code>createOrder()</Code> takes 3 seconds? JavaScript doesn&apos;t wait; it continues immediately, so <Code>processPayment()</Code> runs <Mark>before the order exists</Mark>.</Para>
   <Callout tone="warning" title="The ordering breaks">We need a way to delay execution until a task actually finishes — not just until the line above it returns.</Callout>
  </section>

  {/* 3. Definition */}
  <section className="mb-12">
   <SectionHeading n={3}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">A <Mark>callback</Mark> is a function passed to another function that will be executed later, when a specific task completes.</p>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `function greet(callback) {\n  console.log("Hello");\n  callback();\n}\n\ngreet(() => {\n  console.log("Welcome");\n});` }]} defaultLanguage="javascript" id="cb-def" />
   <Output>{`Hello\nWelcome`}</Output>
   <Para>The function passed into <Code>greet</Code> is the callback.</Para>
  </section>

  {/* 4. Core Idea */}
  <section className="mb-12">
   <SectionHeading n={4}>The Core Idea</SectionHeading>
   <Para>A callback says <Mark>&quot;call me back when you&apos;re done&quot;</Mark> instead of &quot;wait here.&quot; JavaScript keeps working and runs the callback later.</Para>
   <TwoUp
    left={{ title: 'Without callbacks', items: ['Wait here', 'Block the thread', 'Nothing else runs'] }}
    right={{ title: 'With callbacks', items: ['Continue working', 'Task finishes in the background', 'Callback runs later'] }}
   />
   <Para>This idea became the foundation of asynchronous JavaScript.</Para>
  </section>

  {/* 5. Internal Working */}
  <section className="mb-12">
   <SectionHeading n={5}>Internal Working</SectionHeading>
   <Para>When you register an async callback, the engine doesn&apos;t hold the thread. It hands the work to the runtime and the callback is delivered back later through the event loop:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `setTimeout(() => {\n  console.log("Done");\n}, 2000);` }]} defaultLanguage="javascript" id="cb-internal-code" />
   <ol className="my-4 space-y-1.5 text-[14px] text-gray-700 dark:text-gray-300 list-decimal list-inside">
    <li>JavaScript encounters <Code>setTimeout()</Code>.</li>
    <li>The browser/Node runtime starts the timer.</li>
    <li>JavaScript continues executing the rest of the code.</li>
    <li>The timer completes after 2 seconds.</li>
    <li>The callback enters the callback queue.</li>
    <li>The event loop moves it to the call stack when empty.</li>
    <li>The callback executes.</li>
   </ol>
   <Para>That pipeline — runtime API → callback queue → event loop → call stack — is what powers asynchronous JavaScript.</Para>
  </section>

  {/* 6. Visual Flow */}
  <section className="mb-12">
   <SectionHeading n={6}>Visual Flow</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `JavaScript\n   ↓\nRegister timer\n   ↓\nContinue execution\n   ↓\nTimer completes\n   ↓\nCallback queue\n   ↓\nCall stack (when empty)\n   ↓\nExecute callback` }]} defaultLanguage="text" id="cb-flow" />
  </section>

  {/* 7. Simple Example */}
  <section className="mb-12">
   <SectionHeading n={7}>Simple Example</SectionHeading>
   <Para>Callbacks aren&apos;t only asynchronous — they can run immediately too.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function processUser(name, callback) {\n  callback(name);\n}\n\nprocessUser("Sachin", (user) => {\n  console.log(\`Welcome \${user}\`);\n});` }]} defaultLanguage="javascript" id="cb-sync" />
   <Output>{`Welcome Sachin`}</Output>
   <Para>Here the callback executes synchronously, right when it&apos;s called.</Para>
   <SubHeading>Reference vs execution</SubHeading>
   <Para>A classic mistake — passing the <em>result</em> of a call instead of the function:</Para>
   <TwoUp
    left={{ title: '✅ Pass a reference', items: ['processUser("Sachin", callback)', 'callback → the function itself', 'Called later, by the receiver'] }}
    right={{ title: '❌ Call it immediately', items: ['processUser("Sachin", callback())', 'callback() → runs now', 'Passes its return value instead'] }}
   />
   <Callout tone="warning" title="Remember"><Code>callback</Code> is a reference; <Code>callback()</Code> executes it. Pass the reference and let the receiver decide when to invoke it.</Callout>
  </section>

  {/* 8. Deep Example */}
  <section className="mb-12">
   <SectionHeading n={8}>Deep Example: Asynchronous Callbacks</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("Start");\n\nsetTimeout(() => {\n  console.log("Timer Finished");\n}, 2000);\n\nconsole.log("End");` }]} defaultLanguage="javascript" id="cb-async" />
   <Output>{`Start\nEnd\nTimer Finished`}</Output>
   <Para>Most beginners expect <Code>Start → Timer Finished → End</Code>. But <Code>setTimeout</Code> registers the callback and returns immediately, so <Code>End</Code> prints before the callback runs two seconds later.</Para>
   <SubHeading>Coordinating real steps</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `function createOrder(callback) {\n  console.log("Creating Order...");\n  setTimeout(() => {\n    console.log("Order Created");\n    callback();\n  }, 2000);\n}\n\ncreateOrder(() => {\n  console.log("Process Payment");\n});` }]} defaultLanguage="javascript" id="cb-order" />
   <Output>{`Creating Order...\nOrder Created\nProcess Payment`}</Output>
   <Para>Payment begins only after order creation completes — exactly the coordination we needed.</Para>
  </section>

  {/* 9. Production Example */}
  <section className="mb-12">
   <SectionHeading n={9}>Production Example</SectionHeading>
   <Para>Callbacks power real platform APIs. Node&apos;s file system and event handling both use them.</Para>
   <SubHeading>File reading (Node.js)</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fs = require("fs");\n\nfs.readFile("data.txt", "utf8", (error, data) => {\n  if (error) {\n    console.log(error);\n    return;\n  }\n  console.log(data);\n});` }]} defaultLanguage="javascript" id="cb-file" />
   <SubHeading>Browser events</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `button.addEventListener("click", () => {\n  console.log("Button Clicked");\n});` }]} defaultLanguage="javascript" id="cb-event" />
   <SubHeading>Array methods are callbacks too</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const numbers = [1, 2, 3];\n\nnumbers.forEach((num) => {\n  console.log(num);\n});` }]} defaultLanguage="javascript" id="cb-array" />
   <Para>The same goes for <Code>map</Code>, <Code>filter</Code>, <Code>reduce</Code>, <Code>find</Code>, <Code>some</Code>, and <Code>every</Code>. Callbacks are everywhere once you notice them.</Para>
  </section>

  {/* 10. Engineering Tradeoffs */}
  <section className="mb-12">
   <SectionHeading n={10}>Engineering Tradeoffs</SectionHeading>
   <Para>Callbacks are simple, lightweight, and flexible — but they don&apos;t scale elegantly. As an app grows with more async flows, more error handling, and more dependencies, callback-based code becomes harder to manage.</Para>
   <SubHeading>Callback chaining → callback hell</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `createOrder(() => {\n  processPayment(() => {\n    assignDriver(() => {\n      sendNotification(() => {\n        updateAnalytics(() => {\n          saveAuditLog(() => {\n            console.log("Done");\n          });\n        });\n      });\n    });\n  });\n});` }]} defaultLanguage="javascript" id="cb-hell" />
   <Para>The code keeps drifting right into the <Mark>Pyramid of Doom</Mark> — a classic interview term for deeply nested callbacks.</Para>
   <div className="flex flex-wrap gap-2">{['Poor readability', 'Difficult debugging', 'Hard maintenance', 'Difficult testing', 'Scattered error handling'].map((e) => <Pill key={e}>❌ {e}</Pill>)}</div>
   <Para>This pain was one of the biggest reasons promises were introduced.</Para>
  </section>

  {/* 11. Performance Considerations */}
  <section className="mb-12">
   <SectionHeading n={11}>Performance Considerations</SectionHeading>
   <Para>Callbacks themselves are cheap — they&apos;re just function references. What matters for performance is what the callback does and when it runs:</Para>
   <div className="flex flex-wrap gap-2">{['Heavy synchronous work inside a callback still blocks the thread', 'Independent async tasks can run together instead of chaining', 'Deeply nested chains run sequentially even when steps are independent', 'Forgotten error branches leave operations hanging'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
   <Para>Sequential callback chains force each step to wait for the previous one even when they don&apos;t depend on each other — a structural cost that promises and <Code>Promise.all</Code> later made easy to avoid.</Para>
  </section>

  {/* 12. Common Bugs */}
  <section className="mb-12">
   <SectionHeading n={12}>Common Bugs</SectionHeading>
   <Callout tone="warning" title="Calling instead of passing">Passing <Code>callback()</Code> instead of <Code>callback</Code> runs it immediately.</Callout>
   <Callout tone="warning" title="Forgetting error handling">Ignoring the error argument in error-first callbacks lets failures pass silently.</Callout>
   <Callout tone="warning" title="Deep nesting">Letting chains grow into a pyramid instead of refactoring to promises/async-await.</Callout>
   <Callout tone="warning" title="Assuming immediate execution">Expecting an async callback (like <Code>setTimeout</Code>) to run before the code after it.</Callout>
   <Callout tone="warning" title="Misjudging event-loop timing">Not understanding that queued callbacks only run once the call stack is empty.</Callout>
  </section>

  {/* 13. Debugging Stories: error handling + inversion of control */}
  <section className="mb-12">
   <SectionHeading n={13}>Debugging Stories</SectionHeading>
   <SubHeading>The error that vanished</SubHeading>
   <Para>In a deep chain, what happens if payment fails? There&apos;s no shared place to catch errors — each level must forward them manually, which is easy to forget:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `function getUser(callback) {\n  const user = { id: 1, name: "Sachin" };\n  callback(null, user); // error-first: null when successful\n}\n\ngetUser((error, user) => {\n  if (error) {\n    console.log(error);\n    return;\n  }\n  console.log(user);\n});` }]} defaultLanguage="javascript" id="cb-errfirst" />
   <Para>Node standardized this with the <Mark>error-first convention</Mark>: the first argument is the error (or <Code>null</Code>), the second is the result.</Para>
   <SubHeading>The double charge</SubHeading>
   <Para>When you write <Code>apiCall(callback)</Code>, you hand your callback to another function. Now <em>it</em> decides whether, when, and how many times your callback runs — this is <Mark>inversion of control</Mark>. If a payment callback fires twice, the customer is charged twice; if it never fires, the order is stuck forever. These missing execution guarantees were a major motivation for promises.</Para>
  </section>

  {/* 14. TypeScript Example */}
  <section className="mb-12">
   <SectionHeading n={14}>TypeScript Example</SectionHeading>
   <CodeBlock snippets={[{ language: 'typescript', code: `function processUser(name: string, callback: (user: string) => void) {\n  callback(name);\n}\n\nprocessUser("Sachin", (user) => {\n  console.log(user);\n});` }]} defaultLanguage="typescript" id="cb-ts" />
   <div className="flex flex-wrap gap-2">{['Type safety', 'Auto-completion', 'Compile-time checks'].map((e) => <Pill key={e}>{e}</Pill>)}</div>
  </section>

  {/* 15. Java Comparison */}
  <section className="mb-12">
   <SectionHeading n={15}>Java Comparison</SectionHeading>
   <Para>Java expresses the same idea with a callback interface, and modern Java uses lambdas to achieve similar behavior:</Para>
   <CodeBlock snippets={[{ language: 'java', code: `interface Callback {\n  void execute();\n}\n\npublic static void greet(Callback callback) {\n  callback.execute();\n}` }]} defaultLanguage="java" id="cb-java" />
  </section>

  {/* 16. Interview Discussion */}
  <section className="mb-12">
   <SectionHeading n={16}>Interview Discussion</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['What is a callback?', 'Why are callbacks needed?', 'Callback hell?', 'Pyramid of doom?', 'What is inversion of control?', 'Error-first callback pattern?', 'Why were promises introduced?', 'Are callbacks sync or async?', 'Does setTimeout create a thread?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="Are callbacks synchronous or asynchronous?">
    Either. A callback runs synchronously if the receiving function calls it immediately (e.g. <Code>forEach</Code>), or asynchronously if it&apos;s deferred to the runtime (e.g. <Code>setTimeout</Code>, file reads). The pattern itself says nothing about timing.
   </QA>
   <QA q="Does setTimeout create a new thread?">
    No. It hands the timer to a runtime API; when it expires the callback is queued and run on the same single thread via the event loop. JavaScript stays single-threaded.
   </QA>
   <QA q="What is inversion of control?">
    When you pass a callback to another function, that function controls whether, when, and how often your callback runs. You give up those guarantees — risky with third-party code, and a key reason promises were introduced.
   </QA>
   <QA q="What is the error-first callback convention?">
    A Node.js standard where the callback&apos;s first parameter is an error (or <Code>null</Code> on success) and the second is the result: <Code>callback(error, result)</Code>.
   </QA>
  </section>

  {/* 15. Mental Model */}
  <section className="mb-12">
   <SectionHeading n={17}>Mental Model</SectionHeading>
   <Para>Ordering food: you place the order, give your <Mark>phone number</Mark>, and carry on. The restaurant stores it and calls you back when the food is ready. That phone number is your callback — a way to be reached once the task finishes.</Para>
  </section>

  {/* 16. Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={18}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     A callback is a function passed into another function that is executed later when a task completes, forming the foundation of asynchronous and event-driven programming in JavaScript.
    </p>
   </div>
  </section>

  {/* 17. Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={19}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    Callbacks were JavaScript&apos;s first solution to asynchronous programming. They let code run after slow operations complete, but introduced callback hell, complex error handling, and inversion of control. Understanding callbacks is essential — promises and async/await were created specifically to solve these limitations.
   </Callout>
  </section>
 </div>
);

export default JSCallbacks;
